import React, { FunctionComponent, useMemo, useState } from "react";
import { Grid, GridSize, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { IExchangeRate, useGetExchangeRates } from "../../../../hooks/exchangeRates";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifyExchangeRateDialog } from "../ModifyExchangeRateDialog";
import { ExchangeRateCard } from "../ExchangeRateCard";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { useContextMenu } from "../../../../hooks/contextMenu";
import styles from "./styles.module.scss";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { Icon, IconType } from "../../../../icons";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";

export const ExchangeRatesDesktop: FunctionComponent<IExchangeRatesDesktopProps> = ({
  isPublic,
  handleClose,
  columnWidth,
  onExchangeRateSelected
}) => {
  const [loading, setLoading] = useState(true);
  const [exchangeRates, resetExchangeRates, setExchangeRates, noMoreExchangeRates] =
    useGetExchangeRates(setLoading);
  const {
    state: modifyDialogState,
    key: modifyDialogKey,
    openAdd,
    openEdit,
    close: closeModifyDialog
  } = useResettableDialog<IExchangeRate>();
  const [searchText, setSearchText] = useState("");
  const [contextExchangeRate, setContextExchangeRate] = useState<IExchangeRate>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const filteredExchangeRates = useMemo(() => {
    if (searchText == "") {
      return exchangeRates;
    }
    return exchangeRates?.filter(exchangeRate => {
      return exchangeRate.rate.toString().includes(searchText.toLowerCase());
    });
  }, [exchangeRates, searchText]);
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={false}
      items={filteredExchangeRates}
      emptyIconType={attributeIconMap[AttributeType.EXCHANGE_RATES]}
      emptyString={isPublic ? strings.empty_exchange_rates_public : strings.empty_exchange_rates}
      addItemString={strings.add_attribute}
      title={strings.attribute_exchange_rates_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={openAdd}
      refreshAction={resetExchangeRates}
    >
      {filteredExchangeRates?.map(exchangeRate => (
        <Grid item key={exchangeRate.uuid} xs={columnWidth}>
          <ExchangeRateCard
            exchangeRate={exchangeRate}
            onClick={() => {
              if (onExchangeRateSelected) {
                onExchangeRateSelected(exchangeRate);
              } else {
                openEdit(exchangeRate);
              }
            }}
            onContextMenu={event => {
              setContextExchangeRate(exchangeRate);
              handleContextMenu(event);
            }}
          />
        </Grid>
      ))}
      {(loading || !noMoreExchangeRates) &&
        Array.from({ length: 40 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 70 }} className={styles.skeleton} />
          </Grid>
        ))}
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextExchangeRate);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.alert_modify}
        </MenuItem>
      </Menu>
      <ModifyExchangeRateDialog
        key={modifyDialogKey}
        open={modifyDialogState.mode !== "closed"}
        onClose={closeModifyDialog}
        exchangeRate={modifyDialogState.item}
        onNewItem={newExchangeRate => {
          const newExchangeRates = [...exchangeRates, newExchangeRate];
          newExchangeRates.sort((a, b) => b.unixTimestamp - a.unixTimestamp);
          setExchangeRates(newExchangeRates);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={newExchangeRate => {
          const newExchangeRates = exchangeRates.map(exchangeRate =>
            exchangeRate.uuid == newExchangeRate.uuid ? newExchangeRate : exchangeRate
          );
          newExchangeRates.sort((a, b) => b.unixTimestamp - a.unixTimestamp);
          setExchangeRates(newExchangeRates);
          toast.success(strings.generic_update_success);
        }}
        onDeletedItem={deletedExchangeRate => {
          const newExchangeRates = exchangeRates.filter(
            exchangeRate => exchangeRate.uuid != deletedExchangeRate.uuid
          );
          setExchangeRates(newExchangeRates);
          toast.success(strings.generic_update_success);
        }}
      />
    </AttributeDesktop>
  );
};

export interface IExchangeRatesDesktopProps {
  isPublic: boolean;
  handleClose?: () => void;
  columnWidth: GridSize;
  onExchangeRateSelected?: (exchangeRate: IExchangeRate) => void;
}
